import { useCallback, useMemo, useState } from "react";
import { Alert, Paper, Select, Stack, Text } from "@mantine/core";
import type { LegacyDocumentSummary } from "../../../types/legacyCases";
import LegacyDocumentContent from "./LegacyDocumentContent";

interface LegacyDocumentPanelProps {
  documents: LegacyDocumentSummary[];
  initialDocumentId?: string;
}

export default function LegacyDocumentPanel({
  documents,
  initialDocumentId,
}: LegacyDocumentPanelProps) {
  const [selectedId, setSelectedId] = useState(
    initialDocumentId ||
      documents.find((document) => document.kind === "report")?.id ||
      documents[0]?.id ||
      ""
  );
  const selected = useMemo(
    () => documents.find((document) => document.id === selectedId),
    [documents, selectedId]
  );
  const options = useMemo(
    () =>
      documents
        .map((document) => ({
          value: document.id,
          label: `${document.kind === "report" ? "Report" : "Requisition"} — ${
            document.originalFileName
          }`,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [documents]
  );
  const handleSelect = useCallback((value: string | null) => {
    if (value) setSelectedId(value);
  }, []);

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Select
          label="Original document"
          data={options}
          value={selectedId || null}
          onChange={handleSelect}
          allowDeselect={false}
          clearable={false}
          searchable
        />
        {selected ? (
          <LegacyDocumentContent key={selected.id} document={selected} />
        ) : (
          <Alert color="orange" title="No original document">
            A final report must be attached before this case can be imported.
          </Alert>
        )}
        {!documents.some((document) => document.kind === "requisition") && (
          <Text c="dimmed" size="sm">
            No requisition is available for this case.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}
